/**
 * CannedResponsePicker (Phase 37)
 *
 * A categorized, searchable bottom-sheet of canned answers. Opened from the
 * "⚡" trigger in the TicketDetail composer. Tapping an answer INSERTS its
 * body (`body`) into the composer via the `onInsert` callback and closes the
 * sheet — it does NOT send.
 *
 * The search box filters answers by title (`title`) AND body (`body`),
 * case-insensitive, across all categories.
 *
 * Visual chrome reuses the same overlay/sheet Tailwind tokens as the other
 * modals (CloseConfirmModal / the reopen sheet): a fixed inset overlay with a
 * black/40 backdrop and a white rounded sheet anchored to the bottom.
 */

import { useEffect, useMemo, useState } from 'react';
import { Search, Settings, Sparkles, X, Zap } from 'lucide-react';
import { useCannedResponses } from '../../hooks/useCannedResponses';
import type { CannedAnswer, CaseType, PickerCategory } from '../../types';

export interface CannedResponsePickerProps {
  open: boolean;
  onClose: () => void;
  /** Called with the selected answer's body (`body`). Insert-only. */
  onInsert: (body: string) => void;
  /**
   * Phase 38: the focused ticket's case type (`ticket.externalCaseType`,
   * narrowed to a real CaseType or null upstream). When non-null and a loaded
   * category's `caseType` matches it by equality, that category is SURFACED
   * FIRST — pinned to the top of the default list and highlighted with a
   * "Suggested for {label}" affordance. It is a RE-ORDER + HIGHLIGHT, not a
   * filter: every other category stays browsable and search still spans all.
   * Null/undefined (general/organic ticket) or no match → exactly the Phase 37
   * default order, no banner. Matching keys off the caseType only — never the
   * failed-activity chip prefix (that label lives on MessageList, not here).
   */
  suggestedCaseType?: CaseType | null;
  /**
   * L2-only: open the canned-response admin library. When provided, a "Manage"
   * gear button shows in the header — a contextual, in-flow entry to editing
   * the library (closes the picker first). Undefined for level<2 hides it; the
   * real gate is server-side requireLevel(2).
   */
  onManage?: () => void;
}

/**
 * Map a partner case type to the friendly label shown in the
 * "Suggested for {label}" affordance (SV → SIM Verifier, WL → Wallet — the
 * same SOURCE friendlyName semantics used elsewhere).
 */
const CASE_TYPE_LABEL: Record<CaseType, string> = {
  SV: 'SIM Verifier',
  WL: 'Wallet',
};

/** Trim a long body into a one-line preview for the answer row. */
function bodyPreview(body: string): string {
  const oneLine = body.replace(/\s+/g, ' ').trim();
  return oneLine.length > 90 ? `${oneLine.slice(0, 90)}…` : oneLine;
}

export function CannedResponsePicker({
  open,
  onClose,
  onInsert,
  suggestedCaseType = null,
  onManage,
}: CannedResponsePickerProps) {
  const { pickerCategories, isLoading, error, loadPicker } = useCannedResponses();
  const [query, setQuery] = useState('');

  // Load (and re-load) the library every time the sheet opens so a freshly
  // edited/imported answer is reflected without a remount. Reset the search on
  // open so the agent always starts from the full categorized view.
  useEffect(() => {
    if (open) {
      setQuery('');
      void loadPicker();
    }
  }, [open, loadPicker]);

  const trimmedQuery = query.trim().toLowerCase();

  // When searching, produce a flat filtered list of answers (title OR body
  // match). Otherwise keep the full grouped category structure.
  const filteredFlat: CannedAnswer[] = useMemo(() => {
    if (!trimmedQuery) return [];
    const out: CannedAnswer[] = [];
    for (const cat of pickerCategories) {
      for (const ans of cat.answers) {
        if (
          ans.title.toLowerCase().includes(trimmedQuery) ||
          ans.body.toLowerCase().includes(trimmedQuery)
        ) {
          out.push(ans);
        }
      }
    }
    return out;
  }, [pickerCategories, trimmedQuery]);

  // Categories that actually have answers (empty groups are hidden in the
  // picker even though the API returns them with `answers: []`).
  const nonEmptyCategories: PickerCategory[] = useMemo(
    () => pickerCategories.filter((c) => c.answers.length > 0),
    [pickerCategories],
  );

  // Phase 38 — context-aware ordering. When the ticket carries a case type and
  // a loaded category's `caseType` matches it by equality, pin the match(es) to
  // the TOP of the default grouped list (re-order, NOT a filter — the rest stay
  // below in their existing order; if multiple categories share the type, all
  // matches stay first in their existing relative order). Null/no-match falls
  // back to the untouched Phase 37 order.
  const orderedCategories: PickerCategory[] = useMemo(() => {
    if (!suggestedCaseType) return nonEmptyCategories;
    const matched = nonEmptyCategories.filter(
      (c) => c.caseType != null && c.caseType === suggestedCaseType,
    );
    if (matched.length === 0) return nonEmptyCategories;
    const rest = nonEmptyCategories.filter(
      (c) => !(c.caseType != null && c.caseType === suggestedCaseType),
    );
    return [...matched, ...rest];
  }, [nonEmptyCategories, suggestedCaseType]);

  // The friendly label for the "Suggested for {label}" affordance, or null when
  // there is nothing to suggest (no case type, or no category tagged for it).
  const suggestedLabel: string | null = useMemo(() => {
    if (!suggestedCaseType) return null;
    const hasMatch = nonEmptyCategories.some(
      (c) => c.caseType != null && c.caseType === suggestedCaseType,
    );
    return hasMatch ? CASE_TYPE_LABEL[suggestedCaseType] : null;
  }, [nonEmptyCategories, suggestedCaseType]);

  if (!open) return null;

  const handlePick = (ans: CannedAnswer) => {
    onInsert(ans.body);
    onClose();
  };

  const isSearching = trimmedQuery.length > 0;
  const hasAnyAnswers = nonEmptyCategories.length > 0;
  const showEmptyState =
    !isLoading &&
    !error &&
    (isSearching ? filteredFlat.length === 0 : !hasAnyAnswers);

  return (
    <div
      className="fixed inset-0 z-50 flex items-end justify-center"
      onClick={onClose}
      role="dialog"
      aria-modal="true"
      aria-label="Canned responses"
    >
      <div className="absolute inset-0 bg-black/40" aria-hidden="true" />

      <div
        className="relative w-full sm:max-w-md bg-white rounded-t-2xl shadow-2xl flex flex-col max-h-[75vh] safe-area-bottom"
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div className="flex items-center justify-between px-4 pt-4 pb-2">
          <h2 className="flex items-center gap-2 text-lg font-semibold text-gray-900">
            <Zap className="w-5 h-5 text-[#007AFF]" strokeWidth={2} aria-hidden />
            Quick replies
          </h2>
          <div className="flex items-center gap-1">
            {/* L2-only Manage entry — opens the admin library. Closes the picker
                first so the agent lands directly in the editor. */}
            {onManage ? (
              <button
                type="button"
                onClick={() => {
                  onClose();
                  onManage();
                }}
                aria-label="Manage quick replies"
                className="h-9 px-2.5 flex items-center gap-1.5 rounded-full text-[13px] font-medium text-[#007AFF] hover:bg-[#007AFF]/10 active:bg-[#007AFF]/20 touch-active"
              >
                <Settings className="w-4 h-4" strokeWidth={2} aria-hidden />
                Manage
              </button>
            ) : null}
            <button
              type="button"
              onClick={onClose}
              aria-label="Close"
              className="w-9 h-9 -mr-1 flex items-center justify-center rounded-full text-gray-500 hover:bg-gray-100 active:bg-gray-200 touch-active"
            >
              <X className="w-5 h-5" strokeWidth={2} aria-hidden />
            </button>
          </div>
        </div>

        {/* Search */}
        <div className="px-4 pb-3">
          <div className="relative">
            <Search
              className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400"
              strokeWidth={2}
              aria-hidden
            />
            <input
              type="text"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search…"
              aria-label="Search canned responses"
              className="w-full pl-9 pr-3 py-2 text-[15px] rounded-xl bg-gray-100 focus:outline-none focus:ring-2 focus:ring-[#007AFF]/40"
            />
          </div>
        </div>

        {/* Body */}
        <div className="flex-1 overflow-y-auto px-4 pb-4">
          {isLoading && (
            <div className="flex items-center justify-center py-10">
              <span className="w-6 h-6 border-2 border-gray-300 border-t-[#007AFF] rounded-full animate-spin" />
            </div>
          )}

          {!isLoading && error && (
            <div className="flex flex-col items-center gap-3 py-10 text-center">
              <p className="text-sm text-gray-600">{error}</p>
              <button
                type="button"
                onClick={() => void loadPicker()}
                className="px-4 py-2 rounded-xl bg-[#007AFF] text-white text-sm font-medium active:bg-[#0062CC] touch-active"
              >
                Retry
              </button>
            </div>
          )}

          {showEmptyState && (
            <p className="py-10 text-center text-sm text-gray-500">
              {isSearching ? 'No answers found' : 'No quick replies yet'}
            </p>
          )}

          {/* Search results — flat list across categories. */}
          {!isLoading && !error && isSearching && filteredFlat.length > 0 && (
            <ul className="space-y-1">
              {filteredFlat.map((ans) => (
                <AnswerRow key={ans.id} answer={ans} onPick={handlePick} />
              ))}
            </ul>
          )}

          {/* Default — grouped by category. Phase 38: a category whose
              `caseType` matches the ticket's is pinned first (re-order, not a
              filter) and wrapped in a highlighted card with a "Suggested for
              {label}" header. All other categories render below unchanged. */}
          {!isLoading && !error && !isSearching &&
            orderedCategories.map((cat) => {
              const isSuggested =
                suggestedCaseType != null &&
                cat.caseType != null &&
                cat.caseType === suggestedCaseType;

              if (isSuggested) {
                return (
                  <div
                    key={cat.id}
                    className="mb-4 rounded-2xl border border-[#007AFF]/40 bg-[#007AFF]/5 p-2"
                  >
                    <div className="flex items-center gap-1.5 px-1 pt-1 pb-1.5 text-xs font-semibold text-[#007AFF]">
                      <Sparkles className="w-3.5 h-3.5" strokeWidth={2} aria-hidden />
                      <span>
                        Suggested for {suggestedLabel}
                      </span>
                    </div>
                    <h3 className="px-1 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                      {cat.name}
                    </h3>
                    <ul className="space-y-1">
                      {cat.answers.map((ans) => (
                        <AnswerRow key={ans.id} answer={ans} onPick={handlePick} />
                      ))}
                    </ul>
                  </div>
                );
              }

              return (
                <div key={cat.id} className="mb-4">
                  <h3 className="px-1 pt-2 pb-1 text-xs font-semibold uppercase tracking-wide text-gray-500">
                    {cat.name}
                  </h3>
                  <ul className="space-y-1">
                    {cat.answers.map((ans) => (
                      <AnswerRow key={ans.id} answer={ans} onPick={handlePick} />
                    ))}
                  </ul>
                </div>
              );
            })}
        </div>
      </div>
    </div>
  );
}

function AnswerRow({
  answer,
  onPick,
}: {
  answer: CannedAnswer;
  onPick: (a: CannedAnswer) => void;
}) {
  return (
    <li>
      <button
        type="button"
        onClick={() => onPick(answer)}
        className="w-full text-left px-3 py-2.5 rounded-xl hover:bg-gray-50 active:bg-gray-100 touch-active"
      >
        <p className="text-[15px] font-medium text-gray-900 truncate">{answer.title}</p>
        <p className="mt-0.5 text-[13px] text-gray-500 truncate">{bodyPreview(answer.body)}</p>
      </button>
    </li>
  );
}
